import { useState } from 'react';
import PropTypes from 'prop-types';
import { useQuery } from '@tanstack/react-query';
import { Plus, Trash2 } from 'lucide-react';

import { saveManualCollectionPlan } from '#/api/crmMgmt/customerOrder/collection';
import { fetchPaymentTermSelectOptions } from '#/api/system/paymentTerm';
import { cn } from '#/lib/utils';
import { Button } from '#/modules/commonComponents/button';
import { AppDialog } from '#/modules/commonComponents/dialog';
import FloatingDateInput from '#/modules/commonComponents/form/FloatingDateInput';
import FloatingInput from '#/modules/commonComponents/form/FloatingInput';
import { SelectCore } from '#/modules/commonComponents/form/select';
import {
  buildManualPlanPayload,
  planToFormPhases,
  sumAmounts,
  validateManualPlan,
} from '#/modules/crmMgmt/customerOrder/helper/collection';
import { formatDisplayAmount } from '#/utils/formatNumber';
import { lookupQueryKey } from '#/utils/queryKeys';

import useCollectionMutation from './useCollectionMutation';

const EMPTY_PHASE = { value: '', paymentTermId: '', dueDate: '', stablePhaseId: null };

/** 呼叫端每次開啟都重新掛載，表單 state 自然重置。 */
const CollectionPlanEditDialog = ({
  open,
  onOpenChange,
  customerOrderId,
  customerOrderUpdatedAt,
  totalAmount,
  plan,
}) => {
  const initial = planToFormPhases(plan);
  const [inputMode, setInputMode] = useState(initial.inputMode);
  const [phases, setPhases] = useState(
    initial.phases.length > 0 ? initial.phases : [{ ...EMPTY_PHASE, value: '100' }],
  );
  const [formError, setFormError] = useState(null);

  const paymentTermQuery = useQuery({
    queryKey: lookupQueryKey('paymentTermSelect'),
    queryFn: fetchPaymentTermSelectOptions,
    enabled: open,
  });

  const mutation = useCollectionMutation(
    customerOrderId,
    (payload) => saveManualCollectionPlan(customerOrderId, payload),
    { successMessage: '已儲存收款計畫草稿', onSuccess: () => onOpenChange(false) },
  );

  function updatePhase(index, patch) {
    setPhases((rows) => rows.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  }

  function handleConfirm() {
    const error = validateManualPlan({ inputMode, phases, totalAmount });
    setFormError(error);
    if (error || mutation.isPending) {
      return;
    }
    mutation.mutate(buildManualPlanPayload({
      inputMode,
      phases,
      expectedVersionNo: plan?.versionNo ?? null,
      expectedCustomerOrderUpdatedAt: customerOrderUpdatedAt,
    }));
  }

  const unit = inputMode === 'AMOUNT' ? '金額' : '%';
  const total = sumAmounts(phases.map((phase) => phase.value));

  return (
    <AppDialog
      open={open}
      onOpenChange={onOpenChange}
      title="編輯收款分期"
      description="整份送出為新版本草稿，核准後才生效；已核准的舊版在此之前仍有效。到期日請自行填寫，系統不會依付款條件推算。"
      headerVariant="muted"
      maxWidthClassName="sm:max-w-3xl"
      closeOnOutsideClick={false}
      onCancel={() => onOpenChange(false)}
      onConfirm={handleConfirm}
      confirmText={mutation.isPending ? '儲存中…' : '儲存草稿'}
      confirmDisabled={mutation.isPending}
      cancelText="取消"
    >
      <div className="space-y-3 pt-2.5">
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <span className="text-slate-600">輸入方式</span>
          {[['PERCENTAGE', '百分比'], ['AMOUNT', '金額']].map(([value, label]) => (
            <Button
              key={value}
              type="button"
              size="sm"
              variant={inputMode === value ? 'default' : 'outline'}
              onClick={() => setInputMode(value)}
            >
              {label}
            </Button>
          ))}
          <span className="ml-auto text-slate-500">
            客訂總額 {formatDisplayAmount(totalAmount)}
          </span>
        </div>

        {phases.map((phase, index) => (
          <div
            // 分期列沒有穩定 id，以序號為 key
            key={index}
            className={cn(
              'grid grid-cols-1 items-start gap-2 rounded border border-slate-200 p-2',
              'md:grid-cols-[3rem_1fr_1.5fr_1fr_auto]',
            )}
          >
            <span className="pt-2 text-sm text-slate-600">第 {index + 1} 期</span>
            <FloatingInput
              id={`plan-phase-value-${index}`}
              type="number"
              label={unit}
              value={phase.value}
              min={0}
              step="0.01"
              onChange={(event) => updatePhase(index, { value: event.target.value })}
            />
            <SelectCore
              id={`plan-phase-term-${index}`}
              options={[{ value: '', label: '不指定' }, ...(paymentTermQuery.data ?? [])]}
              value={phase.paymentTermId}
              onChange={(value) => updatePhase(index, { paymentTermId: value })}
              label="付款條件（選填）"
            />
            <FloatingDateInput
              id={`plan-phase-due-${index}`}
              label="到期日（選填）"
              mode="single"
              value={phase.dueDate}
              onChange={(value) => updatePhase(index, { dueDate: value ?? '' })}
            />
            <Button
              type="button"
              size="icon"
              variant="ghost"
              aria-label="刪除此期"
              disabled={phases.length <= 1}
              onClick={() => setPhases((rows) => rows.filter((_, i) => i !== index))}
            >
              <Trash2 className="size-4" />
            </Button>
          </div>
        ))}

        <div className="flex items-center justify-between">
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => setPhases((rows) => [...rows, { ...EMPTY_PHASE }])}
          >
            <Plus className="size-4" />
            新增一期
          </Button>
          <span className="text-sm text-slate-600">
            合計 {formatDisplayAmount(total)}
            {inputMode === 'PERCENTAGE' ? ' %' : ''}
          </span>
        </div>
        {formError ? <p className="text-sm text-destructive">{formError}</p> : null}
      </div>
    </AppDialog>
  );
};

CollectionPlanEditDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onOpenChange: PropTypes.func.isRequired,
  customerOrderId: PropTypes.string.isRequired,
  customerOrderUpdatedAt: PropTypes.string,
  totalAmount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  plan: PropTypes.object,
};

export default CollectionPlanEditDialog;
